import {
  MouseEventHandler,
  SyntheticEvent,
  useEffect,
  useState,
  ReactNode
} from "react";

import { useTranslator } from "@eo-locale/react";

import pickBy from "lodash/pickBy";

import identity from "lodash/identity";

import { Alert, Box, Button, CloseButton, Link } from "@/storybook-components";

import { setStatus } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import EmailOrScreenNameQuestion from "../questions/email-or-screen-name-question";
import LoginPasswordQuestion from "../questions/login-password-question";
import { loginRegisteredUser, validateUser } from "@/redux/features/user/thunk";
import { resetRegisterededUser } from "@/redux/features/user/slice";

import { resendUserActivationEmail } from "@/models";
import { fetchAuthToken } from "@/helpers/fetch-auth-token";

const Form = (): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { answers } = useAppSelector((state) => state.questions);
  const { error, status, registeredUser } = useAppSelector(
    (state) => state.user
  );
  const user = useAppSelector((state) => state.user);
  const anonymousUser = useAppSelector((state) => state.anonymousUser);
  const { token } = fetchAuthToken({ user, anonymousUser });
  const { baseUrl } = useAppSelector((state) => state.adapter.adapter);

  const { status: accountStatus, id: registrationId } =
    registeredUser.accountDetails;
  const [isCodeSent, setIsCodeSent] = useState(false);

  const questions = [
    {
      id: "emailOrScreenName",
      type: "EmailOrScreenNameQuestion"
    },
    {
      id: "loginPassword",
      type: "LoginPasswordQuestion"
    }
  ];

  const isLoginQuesionsAnswered =
    questions.length !== Object.values(pickBy(answers, identity)).length;

  useEffect(() => {
    if (accountStatus === "active") {
      dispatch(loginRegisteredUser());
    }
  }, [accountStatus]);

  const handleSubmit = async (e: SyntheticEvent): Promise<void> => {
    e.preventDefault();

    dispatch(resetRegisterededUser());
    dispatch(setStatus("saving"));
    await dispatch(validateUser());
    dispatch(setStatus("idle"));
    setIsCodeSent(false);
  };

  const handleClick = () => {
    resendUserActivationEmail(token, baseUrl, registrationId);
    setIsCodeSent(true);
  };

  const handleCodeSent = () => {
    setIsCodeSent(false);
    dispatch(resetRegisterededUser());
  };

  const RegisterAccountAlertNode = () => {
    if (isCodeSent) {
      return (
        <AlertNode
          status="success"
          description={translate("errors.requestNewEmailInfo")}
          onClose={handleCodeSent}
          alertTestId="login-participant-alert-activation-email-sent"
        />
      );
    }
    if (error) {
      return (
        <AlertNode
          status="error"
          title={translate("errors.invalidLoginOrPassword")}
          onClose={() => dispatch(resetRegisterededUser())}
          alertTestId="login-participant-alert-invalid-credentials"
        />
      );
    }
    if (accountStatus === "not_confirmed") {
      return (
        <AlertNode
          status="warning"
          title={translate("errors.activateYourAccount")}
          description={
            <>
              {translate("errors.activateYourAccountNote")}&nbsp;
              <Link
                textDecoration="underline"
                onClick={handleClick}
                data-testid="login-participant-link-request-activation-email"
              >
                {translate("errors.requestNewEmail")}
              </Link>
            </>
          }
          onClose={() => dispatch(resetRegisterededUser())}
          alertTestId="login-participant-alert-activation-required"
        />
      );
    }
    return null;
  };

  const AlertNode = ({
    title,
    status,
    description,
    onClose,
    alertTestId
  }: {
    title?: string;
    description?: string | ReactNode;
    onClose: MouseEventHandler<HTMLButtonElement>;
    status: "warning" | "success" | "error" | "info";
    alertTestId?: string;
  }) => {
    return (
      <Alert.Root
        status={status}
        borderRadius="md"
        alignItems={description ? "flex-start" : "center"}
        data-testid={alertTestId}
      >
        <Alert.Indicator />
        <Box flex={1}>
          {title && <Alert.Title>{title}</Alert.Title>}
          {description && <Alert.Description>{description}</Alert.Description>}
        </Box>
        <CloseButton
          onClick={onClose}
          data-testid="login-participant-alert-close"
        />
      </Alert.Root>
    );
  };

  return (
    <Box
      as="form"
      noValidate
      onSubmit={handleSubmit}
      data-testid="login-participant-form"
    >
      <EmailOrScreenNameQuestion question={{ id: "emailOrScreenName" }} />
      <LoginPasswordQuestion question={{ id: "loginPassword" }} />
      <RegisterAccountAlertNode />
      <Button
        variant="primary"
        my={3}
        size="lg"
        type="submit"
        width="full"
        loading={status === "fetching"}
        disabled={isLoginQuesionsAnswered}
        data-testid="signIn"
      >
        {translate("login.signIn")}
      </Button>
    </Box>
  );
};

export default Form;
